import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    retry,
    tap,
    throwError,
    timeout,
    TimeoutError,
    timer,
} from 'rxjs';
import { environment } from '../../../../environments/environments';
import { AUTH_SKIP_INTERCEPTOR } from '../../../core/auth';

interface StartConsultationResponse {
    id?: string;
}

export interface ConsultationData {
    gravidade1a5: number;
    queixaPrincipal: string | string[];
    historiaDaDoenca: string | string[];
    antecedentesPessoaisFamiliares: string | string[];
    medicacaoUsoContinuo: string | string[];
    hipotesesDiagnostica: string | string[];
    condutaMedica: string | string[];
    insightNaoFeito: string | string[];
    exameFisico: string;
}

export interface Transcricao {
    consultation_id: string;
    created_at: string;
    transcript_text: string;
    transcription_id: string;
    issuer: 'doctor' | 'patient';
}

interface ConsultationPatient {
    name?: string;
    nome?: string;
    cpf?: string;
    sex?: string;
    sexo?: string;
}

export interface CurrentConsultationResponse extends Partial<ConsultationData> {
    id?: string;
    finishedAt?: string | null;
    patient?: ConsultationPatient;
    paciente?: ConsultationPatient;
    cpf?: string;
    sex?: string;
    sexo?: string;
    patientName?: string;
    patientCpf?: string;
    patientSex?: string;
}

export interface AudioChunkUploadUrlRequest {
    issuer: 'doctor';
    timestamp: number;
    contentType: string;
    lastAudioTime: string;
    totalPausedTime: number;
    isPaused: boolean;
}

export interface PhysicalExamPayload {
    exameFisico: string;
}

interface AudioChunkUploadUrlResponse {
    signedUrl: string;
    headers: Record<string, string>;
}

export const CONSULTATION_ID_STORAGE_KEY = 'ArielConsultationId';

const CONSULTATIONS_BASE_URL = environment.consultasURL;
const CONSULTATIONS_SOURCE_SYSTEM = 'OPTIX';

const HTTP_REQUEST_TIMEOUT_MS = 30_000;
const FINISH_CONSULTATION_MAX_RETRIES = 2;
const FINISH_CONSULTATION_RETRY_DELAY_MS = 1_000;

const getStoredConsultationId = (): string | null => localStorage.getItem(CONSULTATION_ID_STORAGE_KEY);

const setStoredConsultationId = (consultationId: string): void => {
    localStorage.setItem(CONSULTATION_ID_STORAGE_KEY, consultationId);
};

const clearStoredConsultationId = (): void => {
    localStorage.removeItem(CONSULTATION_ID_STORAGE_KEY);
};

const buildSignedUrlUploadHeaders = (headers: Record<string, string>): HttpHeaders => {
    const uploadHeaders: Record<string, string> = {};

    Object.entries(headers).forEach(([name, value]) => {
        const normalizedName = name.toLowerCase();

        if (normalizedName === 'host') {
            return;
        }

        uploadHeaders[name] = value;
    });

    return new HttpHeaders(uploadHeaders);
};

const getErrorMessage = (error: unknown): string => {
    if (!(error instanceof HttpErrorResponse)) {
        return '';
    }

    if (typeof error.error === 'string') {
        return error.error;
    }

    if (typeof error.error?.message === 'string') {
        return error.error.message;
    }

    if (Array.isArray(error.error?.message)) {
        return error.error.message.join(' ');
    }

    return error.message;
};

const isConsultationAlreadyFinishedError = (error: unknown): boolean =>
    getErrorMessage(error).toLowerCase().includes('consulta já finalizada');

const isNoCurrentConsultationError = (error: unknown): boolean =>
    getErrorMessage(error).toLowerCase().includes('nenhuma consulta encontrada');

const isNetworkOrTimeoutError = (error: unknown): boolean =>
    error instanceof TimeoutError || (error instanceof HttpErrorResponse && error.status === 0);

@Injectable()
export class ConsultationsService {

    constructor(
        private readonly http: HttpClient,
    ) {}

    private readonly consultationIdSubject = new BehaviorSubject<string | null>(
        getStoredConsultationId(),
    );

    readonly consultationId$ = this.consultationIdSubject.asObservable();

    get consultationId(): string | null {
        return this.consultationIdSubject.value;
    }

    startNewConsultation(): Observable<string> {
        return this.http
            .post<StartConsultationResponse>(`${CONSULTATIONS_BASE_URL}/start`, {
                sistemaDeOrigem: CONSULTATIONS_SOURCE_SYSTEM,
            })
            .pipe(
                map((consultation) => {
                    if (!consultation.id) {
                        throw new Error('Missing consultation id in start consultation response.');
                    }

                    return consultation.id;
                }),
                tap((consultationId) => this.setConsultationId(consultationId)),
            );
    }

    finishConsultation(consultationId?: string): Observable<void> {
        const consultationIdToFinish = consultationId ?? this.consultationId;

        if (!consultationIdToFinish) {
            return throwError(() => new Error('Missing consultationId to finish consultation.'));
        }

        return this.http
            .put<void>(`${CONSULTATIONS_BASE_URL}/${consultationIdToFinish}/finish`, {
                sistemaDeOrigem: CONSULTATIONS_SOURCE_SYSTEM,
            })
            .pipe(
                timeout(HTTP_REQUEST_TIMEOUT_MS),
                retry({
                    count: FINISH_CONSULTATION_MAX_RETRIES,
                    delay: (error: unknown, retryCount: number) => {
                        if (!isNetworkOrTimeoutError(error)) {
                            return throwError(() => error);
                        }

                        return timer(FINISH_CONSULTATION_RETRY_DELAY_MS * retryCount);
                    },
                }),
                tap(() => this.clearConsultationId()),
                catchError((error: unknown) => {
                    if (isConsultationAlreadyFinishedError(error)) {
                        this.clearConsultationId();
                        return of(void 0);
                    }

                    return throwError(() => error);
                }),
            );
    }

    getCurrentConsultation(): Observable<CurrentConsultationResponse | null> {
        return this.http.get<CurrentConsultationResponse>(`${CONSULTATIONS_BASE_URL}/current`).pipe(
            timeout(HTTP_REQUEST_TIMEOUT_MS),
            map((consultation) => {
                if (!consultation.id || consultation.finishedAt) {
                    this.clearConsultationId();
                    return null;
                }

                this.setConsultationId(consultation.id);
                return consultation;
            }),
            catchError((error: unknown) => {
                if (isNoCurrentConsultationError(error)) {
                    this.clearConsultationId();
                    return of(null);
                }

                return throwError(() => error);
            }),
        );
    }

    syncCurrentConsultation(): Observable<string | null> {
        return this.getCurrentConsultation().pipe(map((consultation) => consultation?.id ?? null));
    }

    getTranscriptions(consultationId: string): Observable<Transcricao[]> {
        return this.http
            .get<Transcricao[] | { transcriptions?: Transcricao[] }>(
                `${CONSULTATIONS_BASE_URL}/${consultationId}/transcriptions`,
            )
            .pipe(
                map((response) => {
                    if (Array.isArray(response)) {
                        return response;
                    }

                    return response.transcriptions ?? [];
                }),
            );
    }

    savePhysicalExam(
        consultationId: string,
        exameFisico: string,
    ): Observable<void> {
        const payload: PhysicalExamPayload = { exameFisico };

        return this.http.put<void>(
            `${CONSULTATIONS_BASE_URL}/${consultationId}/exame-fisico`,
            payload,
        );
    }

    createAudioChunkUploadUrl(
        consultationId: string,
        payload: AudioChunkUploadUrlRequest,
    ): Observable<AudioChunkUploadUrlResponse> {
        return this.http
            .post<AudioChunkUploadUrlResponse>(
                `${CONSULTATIONS_BASE_URL}/${consultationId}/audio-chunks/upload-url`,
                payload,
            )
            .pipe(timeout(HTTP_REQUEST_TIMEOUT_MS));
    }

    uploadAudioChunkToSignedUrl(
        signedUrl: string,
        headers: Record<string, string>,
        chunk: Blob,
    ): Observable<void> {
        return this.http
            .put(signedUrl, chunk, {
                context: new HttpContext().set(AUTH_SKIP_INTERCEPTOR, true),
                headers: buildSignedUrlUploadHeaders(headers),
                responseType: 'text',
            })
            .pipe(timeout(HTTP_REQUEST_TIMEOUT_MS), map(() => void 0));
    }

    setConsultationId(consultationId: string): void {
        setStoredConsultationId(consultationId);
        this.consultationIdSubject.next(consultationId);
    }

    clearConsultationId(): void {
        clearStoredConsultationId();
        this.consultationIdSubject.next(null);
    }
}
